// Small accessible "i" tooltip used beside full-page column headers.
// Opens on hover and keyboard focus. Rendered in a portal with fixed,
// viewport-clamped coordinates so card edges and narrow screens never clip it.
import { useCallback, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { T } from "../../../app/tokens.js";
import { useHoverIntent } from "../../../components/useHoverIntent.js";

const TIP_WIDTH = 250;
const MARGIN = 8;

export function tipPosition(rect, viewportWidth, width = TIP_WIDTH) {
  const w = Math.min(width, viewportWidth - MARGIN * 2);
  const left = Math.max(MARGIN, Math.min(rect.left - 6, viewportWidth - w - MARGIN));
  return { left, top: rect.bottom + 6, width: w };
}

export function InfoTip({ text, label, testId }) {
  const intent = useHoverIntent();
  const open = intent.open;
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const id = useId();
  const place = useCallback(() => {
    if (!ref.current) return;
    setPos(tipPosition(ref.current.getBoundingClientRect(), window.innerWidth || document.documentElement.clientWidth));
  }, []);
  useLayoutEffect(() => {
    if (!open) return undefined;
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => { window.removeEventListener("scroll", place, true); window.removeEventListener("resize", place); };
  }, [open, place]);
  return (
    <span style={{ display: "inline-flex", marginLeft: 7 }} {...intent.triggerProps}>
      <button
        ref={ref}
        type="button"
        aria-label={label}
        aria-describedby={open ? id : undefined}
        data-testid={testId}
        onClick={() => open ? intent.dismiss() : intent.show()}
        style={{
          width: 16, height: 16, borderRadius: "50%", padding: 0,
          border: `1px solid ${T.mid}`, background: "transparent", color: T.mid,
          fontSize: T.fsXxxs, fontWeight: T.fwBold, fontStyle: "italic", cursor: "help",
          display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 1,
        }}
      >
        i
      </button>
      {open && pos && createPortal(
        <span
          role="tooltip"
          id={id}
          data-testid={testId ? `${testId}-text` : undefined}
          style={{
            position: "fixed", left: pos.left, top: pos.top, width: pos.width, zIndex: 1000,
            boxSizing: "border-box", background: T.projectsHubNavy, color: T.white, borderRadius: 8,
            padding: "9px 11px", fontSize: T.fsXs, fontWeight: T.fwSemibold, lineHeight: 1.45,
            letterSpacing: 0, textTransform: "none", whiteSpace: "normal", boxShadow: T.shadowSm,
            pointerEvents: "none",
          }}
        >
          {text}
        </span>,
        document.body,
      )}
    </span>
  );
}
